import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authService';
import apiService from '../services/api';

// Types and Interfaces
export interface UserData {
  id: string;
  email: string;
  name: string;
  role: string;
  twoFactorEnabled?: boolean;
  twoFactorMethod?: string;
  permissions?: string[];
}

export interface MenuStructure {
  id: string;
  name: string;
  parentId?: string;
  description?: string;
  order?: number;
  children?: MenuStructure[];
}

export interface LoginApiResponse {
  status: string;
  message: string;
  data?: {
    email?: string;
    // Identifier (phone number) echoed back for the OTP verification step
    identifier?: string;
    // Masked phone for display, e.g. 0912****567
    maskedPhone?: string;
    requiresOTP?: boolean;
    twoFactorMethod?: string;
    user?: UserData;
    tokens?: {
      accessToken: string;
      refreshToken: string;
    };
  };
}

export interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  isLoading: boolean;
  error: string | null;
  // `identifier` is the admin's mobile phone number (or email for backward compat)
  login: (identifier: string, password: string) => Promise<LoginApiResponse>;
  logout: () => void;
  verifyOtp: (identifier: string, otp: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  resendOtp: (identifier: string) => Promise<boolean>;
  clearError: () => void;
  loginStep1?: (identifier: string, password: string) => Promise<boolean>;
  loginStep2?: (identifier: string, otp: string) => Promise<boolean>;
  isOtpRequired?: boolean;
  identifier?: string;
}

// Response interfaces
interface LoginResponse {
  success: boolean;
  token?: string;
  user?: UserData;
  requiresOTP?: boolean;
  message?: string;
}

interface OtpResponse {
  success: boolean;
  token?: string;
  user?: UserData;
  message?: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message?: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
}

interface ResendOtpResponse {
  success: boolean;
  message?: string;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,
  login: async () => ({ status: 'error', message: 'Not implemented' }),
  logout: () => {},
  verifyOtp: async () => false,
  resetPassword: async () => false,
  forgotPassword: async () => false,
  resendOtp: async () => false,
  clearError: () => {},
});

// No need to instantiate as it's already a singleton
const authService = AuthService;

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading state
  const [error, setError] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState<string>('');
  const [isOtpRequired, setIsOtpRequired] = useState<boolean>(false);
  // menuStructure state removed (Session 3 cutover)
  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const userJson = localStorage.getItem('user');
        if (token && userJson) {
          try {
            const userData = JSON.parse(userJson);
            setUser(userData);
            setIsAuthenticated(true);
          } catch (parseError) {
            console.error('Error parsing user data:', parseError);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (identifier: string, password: string): Promise<LoginApiResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      // ارسال اطلاعات لاگین به سرور
      const response = await authService.adminLogin({ identifier, password });
      const apiResponse = response as LoginApiResponse;

      if (apiResponse.status === 'success') {
        // اگر نیاز به تایید OTP باشد
        if (apiResponse.data?.requiresOTP) {
          // The backend echoes back the identifier (phone) to use in the OTP step
          setIdentifier(apiResponse.data.identifier || identifier);
          setIsOtpRequired(true);
          return apiResponse;
        }
        
        // اگر لاگین موفقیت‌آمیز بوده و داده‌های کاربر و توکن‌ها موجود باشند
        if (apiResponse.data?.user && apiResponse.data?.tokens) {
          const { accessToken, refreshToken } = apiResponse.data.tokens;
          
          if (!accessToken || !refreshToken) {
            setError('خطا در دریافت توکن‌های دسترسی');
            return { status: 'error', message: 'توکن‌های دسترسی دریافت نشد' };
          }
          
          try {
            // Ensure user has permissions property, even if empty
            const userData = {
              ...apiResponse.data.user,
              permissions: apiResponse.data.user.permissions || []
            };
            
            console.log('User data before saving:', userData);
            console.log('User permissions:', userData.permissions);
            
            // ذخیره توکن‌ها و اطلاعات کاربر
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setIsAuthenticated(true);
            
            return apiResponse;
          } catch (err) {
            console.error('Error storing auth data:', err);
            setError('خطا در ذخیره‌سازی اطلاعات احراز هویت');
            return { status: 'error', message: 'خطا در ذخیره‌سازی اطلاعات احراز هویت' };
          }
        } else {
          setError('اطلاعات کاربر یا توکن‌های دسترسی دریافت نشد');
          return { status: 'error', message: 'اطلاعات کاربر یا توکن‌های دسترسی دریافت نشد' };
        }
      } else {
        setError(apiResponse.message || 'خطا در ورود به سیستم');
        return apiResponse;
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'خطای ناشناخته در ورود به سیستم';
      setError(errorMessage);
      return { status: 'error', message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (identifier: string, otp: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // ارسال درخواست تایید OTP به سرور
      const response = await authService.verifyOtp({ identifier, otpCode: otp });
      const apiResponse = response as LoginApiResponse;
      
      if (apiResponse.status === 'success' && apiResponse.data?.user && apiResponse.data?.tokens) {
        const { accessToken, refreshToken } = apiResponse.data.tokens;
        
        if (!accessToken || !refreshToken) {
          setError('خطا در دریافت توکن‌های دسترسی');
          return false;
        }
        
        try {
          // Ensure user has permissions property, even if empty
          const userData = {
            ...apiResponse.data.user,
            permissions: apiResponse.data.user.permissions || []
          };
          
          console.log('User data after OTP:', userData);
          console.log('User permissions after OTP:', userData.permissions);
          
          // ذخیره توکن‌ها و اطلاعات کاربر
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('user', JSON.stringify(userData));
          
          // ذخیره منو استراکچر در localStorage
          setUser(userData);
          setIsAuthenticated(true);
          setIsOtpRequired(false);
          
          // ریدایرکت به داشبورد
          navigate('/');
          
          return true;
        } catch (storageError) {
          console.error('Error storing auth data after OTP:', storageError);
          setError('خطا در ذخیره‌سازی اطلاعات ورود');
          return false;
        }
      } else {
        setError(apiResponse.message || 'تایید کد یکبار مصرف ناموفق بود');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'خطای ناشناخته در تایید کد';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, newPassword: string, confirmPassword: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      // Validate password confirmation match before sending to API
      if (newPassword !== confirmPassword) {
        setError('رمز عبور و تکرار آن باید یکسان باشند');
        return false;
      }
      
      const response = await authService.resetPassword({ token, newPassword });
      const typedResponse = response as ResetPasswordResponse;
      
      if (typedResponse.success) {
        return true;
      } else {
        setError(typedResponse.message || 'Password reset failed');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.forgotPassword(email);
      const typedResponse = response as ForgotPasswordResponse;
      
      if (typedResponse.success) {
        return true;
      } else {
        setError(typedResponse.message || 'Password recovery email failed to send');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async (identifier: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.resendOtp(identifier);
      const typedResponse = response as ResendOtpResponse;
      
      if (typedResponse.success) {
        return true;
      } else {
        setError(typedResponse.message || 'Failed to resend OTP');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // همیشه وضعیت احراز هویت را پاک کنیم، حتی اگر API با خطا مواجه شد
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      // Cutover 2026-05-15: clear the new useAccess module-level cache so the
      // next login (possibly as a different user) doesn't inherit the previous
      // user's permissions snapshot.
      void import('../hooks/useAccess').then((m) => m.clearAccessCache());
      navigate('/login');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const loginStep1 = async (identifier: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setIdentifier(identifier);
    try {
      const response = await authService.adminLogin({ identifier, password });
      const typedResponse = response as LoginResponse;

      if (typedResponse.success) {
        if (typedResponse.requiresOTP) {
          setIsOtpRequired(true);
          // Store identifier in session storage for OTP verification
          sessionStorage.setItem('auth_identifier', identifier);
          return true;
        }
        
        if (typedResponse.token && typedResponse.user) {
          localStorage.setItem('accessToken', typedResponse.token);
          localStorage.setItem('user', JSON.stringify(typedResponse.user));
          setUser(typedResponse.user);
          setIsAuthenticated(true);
          setIsOtpRequired(false);
        }
        return true;
      } else {
        setError(typedResponse.message || 'Login failed');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loginStep2 = async (identifier: string, otp: string): Promise<boolean> => {
    return verifyOtp(identifier, otp);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        error,
        login,
        logout,
        verifyOtp,
        resetPassword,
        forgotPassword,
        resendOtp,
        clearError,
        loginStep1,
        loginStep2,
        isOtpRequired,
        identifier
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext); 